import React from "react";
import { useI18n } from "@/i18n";
import { useReveal } from "@/hooks/useReveal";
import RevealOnView from "@/components/site/RevealOnView";

const Check = () => (
  <svg
    aria-hidden
    viewBox="0 0 24 24"
    className="w-[14px] h-[14px] flex-none"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 12l5 5L20 6" />
  </svg>
);

export default function Pricing() {
  const { t } = useI18n();
  const header = useReveal();

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-36"
    >
      <div className="max-w-[1240px] mx-auto px-5 md:px-8">
        <div
          ref={header.ref}
          className={`reveal ${header.visible ? "is-visible" : ""} max-w-3xl`}
        >
          <div className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 mb-5">
            <span
              className="inline-block w-6 h-px align-middle mr-3"
              style={{ background: "#c9a86a" }}
            />
            {t.pricing.eyebrow}
          </div>
          <h2
            data-testid="pricing-title"
            className="font-display text-[34px] sm:text-[44px] md:text-[56px] leading-[1.05] tracking-[-0.02em] font-light text-neutral-950"
          >
            {t.pricing.title}
          </h2>
          <p className="mt-5 text-neutral-600 text-[15px] md:text-[17px] max-w-xl leading-relaxed">
            {t.pricing.sub}
          </p>
        </div>

        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {t.pricing.plans.map((p, i) => {
            const highlighted = p.highlighted;
            return (
              <RevealOnView
                key={p.id}
                delay={i + 1}
                data-testid={`pricing-card-${p.id}`}
                className={`relative rounded-[14px] p-8 md:p-10 flex flex-col transition-all duration-500 ${
                  highlighted
                    ? "bg-neutral-950 text-white border border-neutral-950"
                    : "bg-white text-neutral-900 border border-neutral-200 hover:-translate-y-1 hover:shadow-[0_30px_80px_-40px_rgba(10,10,10,0.25)]"
                }`}
                style={
                  highlighted
                    ? {
                        boxShadow:
                          "0 40px 100px -40px rgba(10,10,10,0.45), inset 0 0 0 1px rgba(201,168,106,0.4)",
                      }
                    : {}
                }
              >
                {p.badge && (
                  <span
                    data-testid={`pricing-badge-${p.id}`}
                    className="absolute -top-3 left-8 inline-flex items-center gap-2 px-3 py-1 text-[10px] tracking-[0.24em] uppercase rounded-full"
                    style={
                      highlighted
                        ? { background: "#c9a86a", color: "#0a0a0a" }
                        : { background: "#f5f5f5", color: "#737373", border: "1px solid #e5e5e5" }
                    }
                  >
                    {p.badge}
                  </span>
                )}

                <div className="flex items-baseline justify-between">
                  <h3 className="font-display text-[22px] md:text-[24px] tracking-[-0.01em] font-medium">
                    {p.name}
                  </h3>
                </div>

                <div className="mt-6 flex items-baseline gap-2">
                  <span
                    className="font-display text-[48px] md:text-[56px] font-light tracking-[-0.03em]"
                    style={{ lineHeight: 1 }}
                  >
                    {p.price}
                  </span>
                  {p.period && (
                    <span
                      className={`text-[12px] tracking-[0.18em] uppercase ${
                        highlighted ? "text-neutral-400" : "text-neutral-500"
                      }`}
                    >
                      {p.period}
                    </span>
                  )}
                </div>

                <p
                  className={`mt-4 text-[14px] leading-relaxed ${
                    highlighted ? "text-neutral-300" : "text-neutral-600"
                  }`}
                >
                  {p.description}
                </p>

                <div
                  className={`my-8 h-px ${
                    highlighted ? "bg-neutral-800" : "bg-neutral-200"
                  }`}
                />

                <ul className="flex flex-col gap-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[14px]">
                      <span
                        className={`mt-0.5 ${
                          highlighted ? "text-[#c9a86a]" : "text-neutral-900"
                        }`}
                      >
                        <Check />
                      </span>
                      <span
                        className={
                          highlighted ? "text-neutral-100" : "text-neutral-800"
                        }
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <a
                  href="/login"
                  data-testid={`pricing-cta-${p.id}`}
                  className={`mt-10 rounded-full text-center py-3.5 px-6 text-[13px] tracking-wide transition-all duration-300 ${
                    highlighted
                      ? "bg-white text-neutral-950 hover:bg-[#c9a86a] hover:text-neutral-950"
                      : "wf-btn-primary"
                  }`}
                >
                  {p.cta}
                </a>
              </RevealOnView>
            );
          })}
        </div>
      </div>
    </section>
  );
}
